import {
  createContext,
  useCallback,
  useContext,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { AlertTriangle, Info, Trash2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

export type ConfirmVariant = "danger" | "warning" | "info";

export interface ConfirmOptions {
  title: string;
  description: string;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: ConfirmVariant;
  icon?: ReactNode;
  /** When set, the confirm button stays disabled until the user types
   *  this string verbatim — used for high-blast-radius actions where
   *  a single click is too easy. */
  requireInput?: string;
  requireInputLabel?: string;
}

export interface PromptOptions {
  title: string;
  description?: string;
  defaultValue?: string;
  placeholder?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  /** Reject empty input — defaults to true. Trimmed values are
   *  compared against "" before being returned. */
  required?: boolean;
}

interface ConfirmContextType {
  confirm: (options: ConfirmOptions) => Promise<boolean>;
  prompt: (options: PromptOptions) => Promise<string | null>;
}

const ConfirmContext = createContext<ConfirmContextType | null>(null);

export function useConfirm() {
  const ctx = useContext(ConfirmContext);
  if (!ctx) throw new Error("useConfirm must be used within ConfirmProvider");
  return ctx.confirm;
}

export function usePrompt() {
  const ctx = useContext(ConfirmContext);
  if (!ctx) throw new Error("usePrompt must be used within ConfirmProvider");
  return ctx.prompt;
}

type DialogState =
  | { kind: "confirm"; options: ConfirmOptions }
  | { kind: "prompt"; options: PromptOptions }
  | null;

export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<DialogState>(null);
  const [inputValue, setInputValue] = useState("");
  // The promise resolver is held in a ref so consecutive open/close
  // cycles can't race — setState would be async and a fast double-
  // open would land on a stale resolver.
  const resolveConfirmRef = useRef<((value: boolean) => void) | null>(null);
  const resolvePromptRef = useRef<((value: string | null) => void) | null>(
    null,
  );

  const confirm = useCallback(
    (options: ConfirmOptions): Promise<boolean> =>
      new Promise((resolve) => {
        resolveConfirmRef.current = resolve;
        setInputValue("");
        setState({ kind: "confirm", options });
      }),
    [],
  );

  const prompt = useCallback(
    (options: PromptOptions): Promise<string | null> =>
      new Promise((resolve) => {
        resolvePromptRef.current = resolve;
        setInputValue(options.defaultValue ?? "");
        setState({ kind: "prompt", options });
      }),
    [],
  );

  const finishConfirm = (value: boolean) => {
    const resolve = resolveConfirmRef.current;
    resolveConfirmRef.current = null;
    setState(null);
    resolve?.(value);
  };

  const finishPrompt = (value: string | null) => {
    const resolve = resolvePromptRef.current;
    resolvePromptRef.current = null;
    setState(null);
    resolve?.(value);
  };

  const handleOpenChange = (open: boolean) => {
    if (open || !state) return;
    if (state.kind === "confirm") finishConfirm(false);
    else finishPrompt(null);
  };

  const isOpen = state !== null;

  return (
    <ConfirmContext.Provider value={{ confirm, prompt }}>
      {children}
      <Dialog open={isOpen} onOpenChange={handleOpenChange}>
        <DialogContent className="sm:max-w-[420px]">
          {state?.kind === "confirm" && (
            <ConfirmBody
              options={state.options}
              inputValue={inputValue}
              onInputChange={setInputValue}
              onConfirm={() => finishConfirm(true)}
              onCancel={() => finishConfirm(false)}
            />
          )}
          {state?.kind === "prompt" && (
            <PromptBody
              options={state.options}
              value={inputValue}
              onChange={setInputValue}
              onConfirm={(v) => finishPrompt(v)}
              onCancel={() => finishPrompt(null)}
            />
          )}
        </DialogContent>
      </Dialog>
    </ConfirmContext.Provider>
  );
}

const VARIANT_STYLES: Record<
  ConfirmVariant,
  {
    iconBg: string;
    iconColor: string;
    confirmBtn: string;
    defaultIcon: ReactNode;
  }
> = {
  danger: {
    iconBg: "bg-rose-100",
    iconColor: "text-rose-600",
    confirmBtn: "bg-rose-600 hover:bg-rose-700 text-white",
    defaultIcon: <Trash2 className="h-5 w-5" />,
  },
  warning: {
    iconBg: "bg-amber-100",
    iconColor: "text-amber-600",
    confirmBtn: "bg-amber-600 hover:bg-amber-700 text-white",
    defaultIcon: <AlertTriangle className="h-5 w-5" />,
  },
  info: {
    iconBg: "bg-indigo-100",
    iconColor: "text-indigo-600",
    confirmBtn: "bg-indigo-600 hover:bg-indigo-700 text-white",
    defaultIcon: <Info className="h-5 w-5" />,
  },
};

function ConfirmBody({
  options,
  inputValue,
  onInputChange,
  onConfirm,
  onCancel,
}: {
  options: ConfirmOptions;
  inputValue: string;
  onInputChange: (next: string) => void;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const styles = VARIANT_STYLES[options.variant ?? "warning"];
  const inputValid =
    !options.requireInput || inputValue === options.requireInput;
  return (
    <>
      <DialogHeader>
        <div className="flex items-start gap-4">
          <div
            className={cn(
              "flex h-10 w-10 shrink-0 items-center justify-center rounded-full",
              styles.iconBg,
              styles.iconColor,
            )}
          >
            {options.icon ?? styles.defaultIcon}
          </div>
          <div className="min-w-0">
            <DialogTitle className="text-lg">{options.title}</DialogTitle>
            <DialogDescription className="mt-1.5 text-sm text-slate-500">
              {options.description}
            </DialogDescription>
          </div>
        </div>
      </DialogHeader>

      {options.requireInput && (
        <div className="mt-4 space-y-2">
          <label className="text-sm text-slate-600">
            {options.requireInputLabel ??
              `Type "${options.requireInput}" to confirm`}
          </label>
          <Input
            value={inputValue}
            onChange={(e) => onInputChange(e.target.value)}
            placeholder={options.requireInput}
            className={cn(
              "font-mono",
              inputValue &&
                inputValue !== options.requireInput &&
                "border-rose-300 focus-visible:ring-rose-400",
            )}
            autoFocus
            onKeyDown={(e) => {
              if (e.key === "Enter" && inputValid) onConfirm();
            }}
          />
        </div>
      )}

      <DialogFooter className="mt-6 gap-2 sm:gap-2">
        <Button variant="outline" onClick={onCancel}>
          {options.cancelLabel ?? "Cancel"}
        </Button>
        <Button
          onClick={onConfirm}
          disabled={!inputValid}
          className={cn(
            styles.confirmBtn,
            !inputValid && "cursor-not-allowed opacity-50",
          )}
        >
          {options.confirmLabel ?? "Confirm"}
        </Button>
      </DialogFooter>
    </>
  );
}

function PromptBody({
  options,
  value,
  onChange,
  onConfirm,
  onCancel,
}: {
  options: PromptOptions;
  value: string;
  onChange: (next: string) => void;
  onConfirm: (value: string) => void;
  onCancel: () => void;
}) {
  const trimmed = value.trim();
  const required = options.required ?? true;
  const canSubmit = required ? trimmed.length > 0 : true;
  return (
    <>
      <DialogHeader>
        <DialogTitle className="text-lg">{options.title}</DialogTitle>
        {options.description && (
          <DialogDescription className="mt-1.5 text-sm text-slate-500">
            {options.description}
          </DialogDescription>
        )}
      </DialogHeader>

      <Input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={options.placeholder}
        autoFocus
        onKeyDown={(e) => {
          if (e.key === "Enter" && canSubmit) onConfirm(trimmed);
        }}
        className="mt-2"
      />

      <DialogFooter className="mt-4 gap-2 sm:gap-2">
        <Button variant="outline" onClick={onCancel}>
          {options.cancelLabel ?? "Cancel"}
        </Button>
        <Button
          onClick={() => onConfirm(trimmed)}
          disabled={!canSubmit}
          className={cn(!canSubmit && "cursor-not-allowed opacity-50")}
        >
          {options.confirmLabel ?? "OK"}
        </Button>
      </DialogFooter>
    </>
  );
}
